import { indexUrl, isTimeLayer, parseDuration } from './timeline'

/**
 * Loads time layers' index.json. A found index is reused until it is older than its step
 * (at least a minute), which is also the CDN's max-age for it. A 404 means the index is
 * not published yet, so the layer is asked again after five minutes.
 */
const entries = new Map()       // layerId -> { index, fetched, maxAge }
const missingSince = new Map()
const inFlight = new Map()

export function fetchTimeLayerIndex(layer) {
  if (!isTimeLayer(layer)) return Promise.resolve(null)
  const url = indexUrl(layer.cloudfrontUrl)
  if (!url) return Promise.resolve(null)
  const since = missingSince.get(layer.id)
  if (since && Date.now() - since < 300000) return Promise.resolve(null)
  const entry = entries.get(layer.id)
  if (entry && Date.now() - entry.fetched < entry.maxAge) return Promise.resolve(entry.index)
  if (inFlight.has(layer.id)) return inFlight.get(layer.id)

  const request = fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-cache' })
    .then(res => {
      if (res.status === 404) {
        missingSince.set(layer.id, Date.now())
        entries.delete(layer.id)
        return null
      }
      if (!res.ok) throw new Error(`index.json HTTP ${res.status}`)
      return res.json().then(index => {
        const step = parseDuration(index.step) || 60
        entries.set(layer.id, { index, fetched: Date.now(), maxAge: Math.max(60, step) * 1000 })
        missingSince.delete(layer.id)
        return index
      })
    })
    .catch(err => {
      // A timeout or bad body: keep the last good index if there is one, retry next time.
      console.warn('TimeLayers: index.json failed for layer', layer.id, err)
      return entries.get(layer.id)?.index ?? null
    })
    .finally(() => inFlight.delete(layer.id))
  inFlight.set(layer.id, request)
  return request
}

/**
 * index.json for a tile layer that is not necessarily on the map (USA:usa_wind, which
 * precipitation drift reads even when the Wind layer is off), by its tiles base URL.
 * Same reuse rule as above; a failure keeps the last good index.
 */
const byUrl = new Map()   // url -> { index, fetched, maxAge, request }

export function fetchIndexAt(base) {
  if (!base) return Promise.resolve(null)
  const url = `${base}index.json`
  const entry = byUrl.get(url)
  if (entry && entry.request) return entry.request
  if (entry && entry.index && Date.now() - entry.fetched < entry.maxAge) return Promise.resolve(entry.index)
  const request = fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-cache' })
    .then(res => (res.ok ? res.json() : null))
    .then(index => {
      if (index) {
        const step = parseDuration(index.step) || 60
        byUrl.set(url, { index, fetched: Date.now(), maxAge: Math.max(60, step) * 1000 })
      } else {
        byUrl.set(url, { index: entry?.index ?? null, fetched: Date.now(), maxAge: 60000 })
      }
      return byUrl.get(url).index
    })
    .catch(() => {
      byUrl.set(url, { index: entry?.index ?? null, fetched: Date.now(), maxAge: 60000 })
      return entry?.index ?? null
    })
  byUrl.set(url, { ...(entry || {}), request })
  return request
}
